import { Id } from '@/infra/id'
import { Conversation } from '@/features/messaging/Conversation'
import { type Member } from '@/features/iam/members/Member'
import { unixEpoch } from '@/utils/date-util'
import { collection } from '@/infra/collection'
import { Model, type ModelProps } from '@/infra/model'
import { RoomSession, type RoomSessionProps } from './RoomSession'
import { action, observable } from 'mobx'
import { once } from 'lodash-es'

export interface RoomProps extends Omit<ModelProps<Room>, 'sessions'> {
  sessions?: RoomSessionProps[]
}

/**
 * A consultation room, where a call about a conversation takes place.
 */
export class Room extends Model<Room> {
  id = Id.none

  @observable accessor conversation = Conversation.none
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()
  @observable accessor status: RoomStatus = 'Unknown'
  @observable accessor roomSource: RoomSource = {
    type: 'Unknown',
  }
  @observable.shallow accessor activeParticipants: Member[] = []

  readonly sessions = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new RoomSession(),
  })

  static get none(): Room {
    return lazyNone()
  }

  @action.bound
  override set({ sessions, ...props }: Partial<RoomProps>): this {
    this.sessions.reset(sessions)
    return super.set(props)
  }
}

/**
 * Whether a room is open for calls.
 */
export type RoomStatus = 'Unknown' | 'Active' | 'Closed'

/**
 * The service that hosts a room.
 */
export type RoomSource = WherebyRoomSource | LocalRoomSource | UnknownRoomSource

export interface LocalRoomSource {
  type: 'Local'
}

export interface WherebyRoomSource {
  type: 'Whereby'
}

export interface UnknownRoomSource {
  type: 'Unknown'
}

const lazyNone = once(() => new Room())
